import { useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTransactionBuilderStore } from './useTransactionBuilderStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useNavigate } from 'react-router';
import { NetworkIcon } from '@/components/shared/network-icon';
import { formatAddress } from '@/lib/utils';
import { getNetworkDisplayName } from '@mls-wallet/shared-types';

/**
 * Props for the TransactionBuilderHeader component
 */
interface TransactionBuilderHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBack?: () => void;
}

/**
 * Header component for the transaction builder
 * Displays title, back button (optional), and close button with confirmation dialog
 */
export function TransactionBuilderHeader({
  title,
  showBackButton = false,
  onBack,
}: TransactionBuilderHeaderProps): React.ReactElement {
  const navigateBackward = useTransactionBuilderStore(
    (state) => state.navigateBackward
  );
  const account = useTransactionBuilderStore((state) => state.account);
  const network = useTransactionBuilderStore((state) => state.network);
  const reset = useTransactionBuilderStore((state) => state.reset);

  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const navigate = useNavigate();

  const handleBackClick = () => {
    (onBack ?? navigateBackward)();
  };

  const handleCloseClick = () => {
    setIsCloseDialogOpen(true);
  };

  const handleCloseDialogClose = () => {
    setIsCloseDialogOpen(false);
  };

  const handleExitTransactionBuilder = () => {
    setIsCloseDialogOpen(false);
    reset();
    // Navigate back to home page
    navigate('/');
  };

  return (
    <>
      <div className="w-full top-0 z-10 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
        {/* Selected Account Indicator */}
        <div className="flex justify-center pt-[20px]">
          <div className="bg-neutral-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[45px] px-[6px] pl-[6px] pr-[10px] py-[3px]">
            <div className="flex gap-[10px] items-center min-h-[17px]">
              {account ? (
                <>
                  {/* Network pill - only shown when network is selected */}
                  {network && (
                    <div className="flex gap-[3px] items-center bg-zinc-200 dark:bg-zinc-700 rounded-[32px] px-[7px] py-[2px]">
                      <NetworkIcon
                        networkId={network}
                        size="xs"
                        className="text-zinc-700 dark:text-zinc-300"
                      />
                      <p className="font-medium text-[10px] text-zinc-700 dark:text-zinc-300 leading-normal">
                        {getNetworkDisplayName(network)}
                      </p>
                    </div>
                  )}
                  {/* Account name and address - centered together */}
                  <div className="flex items-center gap-[10px]">
                    <p className="font-medium text-[12px] text-zinc-700 dark:text-zinc-300 leading-normal pl-[3px]">
                      {account.name}
                    </p>
                    <p className="font-normal font-mono text-[10px] text-zinc-600 dark:text-zinc-400 leading-normal">
                      {formatAddress(account.address)}
                    </p>
                  </div>
                </>
              ) : (
                /* No account selected message */
                <p className="font-medium text-[12px] text-zinc-400 dark:text-zinc-400 leading-normal pl-[3px]">
                  No account selected
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Title row with back and close buttons */}
        <div className="grid min-h-[67px] grid-cols-[48px_minmax(0,1fr)_48px] items-center px-3 pb-[5px] sm:px-[27px]">
          {/* Back button */}
          <div className="flex items-center">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBackClick}
                className="h-12 w-12 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
                <span className="sr-only">Back</span>
              </Button>
            )}
          </div>
          {/* Title */}
          <div>
            <h4 className="text-center text-base font-medium sm:text-[20px]">
              {title}
            </h4>
          </div>
          {/* Close button */}
          <div className="flex">
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-12 rounded-full"
              aria-label="Exit transaction builder"
              onClick={handleCloseClick}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Exit transaction builder dialog */}
      <Dialog open={isCloseDialogOpen} onOpenChange={setIsCloseDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>You have unsaved changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to exit the transaction builder?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleCloseDialogClose}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleExitTransactionBuilder}
              className="w-full sm:w-auto"
            >
              Exit transaction builder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
